import { makeAutoObservable, reaction } from 'mobx';

import { pureStructureFrom } from '@/common/helpers/pureStructureFrom';
import useStore from '@/common/hooks/useStore';
import LocalStorageDriver from '@/common/services/session/LocalStorageDriver';
import UserSessionDataManager from '@/common/services/session/UserSessionDataManager';
import AppStore, { appStore } from '@/common/stores/AppStore';
import { COMPARISON_LIST_STORAGE_KEY, EMPTY_COMPARISON_STATE } from '@/comparison/constants/context';
import { getCompareContextByType } from '@/comparison/helpers/getCompareContextByType';
import { CompareMapType, CompareProductEntity, CompareStorageType } from '@/comparison/types';
import { ProductType } from '@/product/constants/productTypes';

import ComparisonListSerializer from '../serializers/ComparisonListSerializer';

class ComparisonStore {
  // Data structure to store all added products to compare
  productMap: CompareMapType = pureStructureFrom(EMPTY_COMPARISON_STATE);

  private readonly appStore: AppStore;
  private readonly userSessionDataManager: UserSessionDataManager;

  constructor(appStore: AppStore) {
    makeAutoObservable<ComparisonStore, 'appStore' | 'userSessionDataManager'>(this, {
      appStore: false,
      userSessionDataManager: false,
      overallCountOfProducts: false,
      products: false,
      productIds: false,
      productsCount: false,
      isExist: false,
    });

    this.appStore = appStore;

    // initialize storage
    this.userSessionDataManager = new UserSessionDataManager(new LocalStorageDriver());
    this.userSessionDataManager.get<CompareStorageType>(COMPARISON_LIST_STORAGE_KEY).then(this.syncProductMapWithJSON);
    this.userSessionDataManager.onKeyChange(COMPARISON_LIST_STORAGE_KEY, this.syncProductMapWithJSON);

    reaction(
      () => this.overallCountOfProducts,
      () => {
        this.userSessionDataManager.set(
          COMPARISON_LIST_STORAGE_KEY,
          ComparisonListSerializer.serialize(this.productMap),
        );
      },
    );
  }

  get overallCountOfProducts() {
    let overallCount = 0;

    Object.keys(this.productMap).forEach((productTypeKey) => {
      const type = this.productMap[getCompareContextByType(productTypeKey as ProductType)];

      overallCount += type ? type.length : 0;
    });

    return overallCount;
  }

  get products() {
    return this.productMap[getCompareContextByType(this.appStore.productTypeContext)];
  }

  get productIds() {
    return this.products?.map((item) => item.id) ?? [];
  }

  get productsCount() {
    return this.products ? this.products.length : 0;
  }

  syncProductMapWithJSON = (jsonProductList: CompareStorageType) => {
    const deserializedProductList = ComparisonListSerializer.deserialize(jsonProductList);

    this.productMap = { ...pureStructureFrom(EMPTY_COMPARISON_STATE), ...deserializedProductList };
  };

  toggleProduct = (product: CompareProductEntity) => {
    if (this.isExist(product)) {
      this.removeProduct(product);
    } else {
      this.addProduct(product);
    }
  };

  addProduct(product: CompareProductEntity) {
    if (!this.isExist(product)) {
      this.productMap[getCompareContextByType(product.type)].push(product);
    }
  }

  removeProduct(product: CompareProductEntity) {
    this.removeProductById(product.id);
  }

  removeProductById(id: number) {
    Object.keys(this.productMap).forEach((type) => {
      const context = getCompareContextByType(type as ProductType);

      if (this.productMap[context]) {
        const index = this.productMap[context]!.findIndex((item) => item.id === id);

        if (index === -1) {
          return;
        }

        this.productMap[context]!.splice(index, 1);
      }
    });
  }

  clearCompare() {
    this.productMap[getCompareContextByType(this.appStore.productTypeContext)] = [];
  }

  isExist = (product: CompareProductEntity) => (
    this.productMap[getCompareContextByType(product.type)]?.some((item) => item.id === product.id)
  );
}

export const useComparisonStore = () => useStore(ComparisonStore);

export const comparisonStore = new ComparisonStore(appStore);
export default ComparisonStore;
